import Box from "@mui/material/Box";
import * as d3 from "d3";
import React, { useLayoutEffect, useRef, useState } from "react";

function fontSizeForCircle(r, label, { maxPx = 10, diameterUse = 0.82, charWidthRatio = 0.62, lineHeightRatio = 1.22 } = {}) {
    const text = String(label ?? "");
    const len = Math.max(text.length, 1);
    const usableDiameter = 2 * r * diameterUse;
    const byWidth = usableDiameter / (len * charWidthRatio);
    const byHeight = usableDiameter / lineHeightRatio;
    const fit = Math.min(byWidth, byHeight);
    return Math.min(maxPx, fit);
}

const PARENT_R_SCALE = 0.74;
const CLUSTER_HUES = [268, 168, 32, 312, 198, 12];

/**
 * Clustered bubble pack for puzzle cards: centered layout, scroll/pinch zoom, drag to pan.
 * @param {object} clusteredBubbles — root { name, children } from template.clusteredBubbles
 * @param {boolean} [fillAvailableHeight] — grow with parent flex; height from container (puzzle cards in a row)
 */
export default function ClusteredBubblesMiniChart({ clusteredBubbles, fillAvailableHeight = false }) {
    const hostRef = useRef(null);
    const [chartHeight, setChartHeight] = useState(96);

    useLayoutEffect(() => {
        const el = hostRef.current;
        if (!clusteredBubbles?.children?.length || !el) return undefined;

        const buildChart = () => {
            const w = Math.max(100, Math.floor(el.clientWidth || el.getBoundingClientRect().width || 200));
            const rawH = Math.floor(el.clientHeight || 0);
            const h =
                fillAvailableHeight && rawH >= 64
                    ? rawH
                    : Math.min(140, Math.max(88, Math.round(w * 0.36)));
            if (!fillAvailableHeight) {
                setChartHeight(h);
            }

            el.innerHTML = "";

            const svg = d3
                .select(el)
                .append("svg")
                .attr("width", w)
                .attr("height", h)
                .attr("viewBox", `0 0 ${w} ${h}`)
                .style("display", "block")
                .style("cursor", "grab")
                .style("touch-action", "none")
                .attr("role", "img")
                .attr("aria-label", "Clustered word bubbles");

            const hierarchy = d3
                .hierarchy(clusteredBubbles)
                .sum((d) => (typeof d.value === "number" ? d.value : 0))
                .sort((a, b) => (b.value || 0) - (a.value || 0));

            const pack = d3.pack().size([w, h]).padding(2);
            pack(hierarchy);

            const clusterKey = (d) => {
                let n = d;
                while (n.parent && n.parent.depth > 0) {
                    if (n.parent.depth === 1) return String(n.parent.data.name ?? "");
                    n = n.parent;
                }
                return n.depth === 1 ? String(n.data.name ?? "") : "cluster";
            };

            const clusters = [
                ...new Set(
                    hierarchy
                        .descendants()
                        .filter((d) => d.depth >= 1)
                        .map(clusterKey)
                        .filter(Boolean)
                ),
            ];

            const leaves = hierarchy.leaves();
            const leafValues = leaves.map((l) => l.value || 0);
            const vMin = d3.min(leafValues) ?? 0;
            const vMax = d3.max(leafValues) ?? 1;
            const normLeafValue = (v) => {
                if (vMax - vMin < 1e-9) return 0.5;
                return (v - vMin) / (vMax - vMin);
            };

            const hueByCluster = d3.scaleOrdinal(CLUSTER_HUES).domain(clusters.length ? clusters : ["default"]);

            const leafFill = (d) => {
                const t = normLeafValue(d.value || 0);
                const hue = hueByCluster(clusterKey(d));
                const sat = 0.38 + 0.48 * t;
                const light = 0.78 - 0.36 * t;
                return d3.hsl(hue, sat, light).formatHex();
            };

            const displayRadius = (d) => (d.children ? d.r * PARENT_R_SCALE : d.r);

            const nodes = hierarchy.descendants().filter((d) => d.depth > 0);

            let xMin = Infinity;
            let xMax = -Infinity;
            let yMin = Infinity;
            let yMax = -Infinity;
            for (const d of nodes) {
                const r = displayRadius(d);
                xMin = Math.min(xMin, d.x - r);
                xMax = Math.max(xMax, d.x + r);
                yMin = Math.min(yMin, d.y - r);
                yMax = Math.max(yMax, d.y + r);
            }
            if (!Number.isFinite(xMin) || !Number.isFinite(xMax)) {
                xMin = 0;
                xMax = w;
                yMin = 0;
                yMax = h;
            }
            const midX = (xMin + xMax) / 2;
            const midY = (yMin + yMax) / 2;
            const tx = w / 2 - midX;
            const ty = h / 2 - midY;

            const zoomLayer = svg.append("g").attr("class", "zoom-layer");
            const gMain = zoomLayer.append("g").attr("transform", `translate(${tx},${ty})`);

            const nodeG = gMain.selectAll("g.buggle-mini").data(nodes).join("g").attr("class", "buggle-mini");

            nodeG
                .append("circle")
                .attr("cx", (d) => d.x)
                .attr("cy", (d) => d.y)
                .attr("r", (d) => displayRadius(d))
                .attr("fill", (d) => {
                    if (d.children) {
                        const hue = hueByCluster(clusterKey(d));
                        return d3.hsl(hue, 0.12, 0.94).formatRgb();
                    }
                    return leafFill(d);
                })
                .attr("stroke", (d) => {
                    if (d.children) return "rgba(0,0,0,0.12)";
                    const c = d3.color(leafFill(d));
                    return c ? c.darker(0.35).formatRgb() : "rgba(0,0,0,0.2)";
                })
                .attr("stroke-width", (d) => (d.children ? 0.75 : 0.85))
                .attr("opacity", (d) => (d.children ? 0.5 : 0.96));

            const leafLabelMinR = 6;
            nodeG
                .filter((d) => !d.children && d.r > leafLabelMinR)
                .append("text")
                .text((d) => d.data.name ?? "")
                .attr("x", (d) => d.x)
                .attr("y", (d) => d.y)
                .attr("text-anchor", "middle")
                .attr("dy", "0.35em")
                .attr("font-size", (d) => fontSizeForCircle(d.r, d.data.name, { maxPx: 9 }))
                .attr("font-weight", "600")
                .attr("fill", (d) => (normLeafValue(d.value || 0) > 0.52 ? "#f8fafc" : "#0f172a"))
                .style("pointer-events", "none");

            nodeG
                .filter((d) => d.children && d.depth === 1 && d.r > 10)
                .append("text")
                .text((d) => d.data.name ?? "")
                .attr("x", (d) => d.x)
                .attr("y", (d) => d.y)
                .attr("text-anchor", "middle")
                .attr("dy", "0.35em")
                .attr("font-size", (d) =>
                    fontSizeForCircle(displayRadius(d), d.data.name, {
                        maxPx: 11,
                        charWidthRatio: 0.65,
                        lineHeightRatio: 1.2,
                    })
                )
                .attr("font-weight", "700")
                .attr("fill", "rgba(0,0,0,0.5)")
                .style("pointer-events", "none");

            const zoom = d3
                .zoom()
                .scaleExtent([0.45, 5])
                .translateExtent([
                    [-w * 2, -h * 2],
                    [w * 3, h * 3],
                ])
                .on("start", () => {
                    svg.style("cursor", "grabbing");
                })
                .on("end", () => {
                    svg.style("cursor", "grab");
                })
                .on("zoom", (event) => {
                    zoomLayer.attr("transform", event.transform);
                });

            svg.call(zoom);

            return () => {
                svg.on(".zoom", null);
            };
        };

        let teardownZoom = () => {};
        const run = () => {
            teardownZoom();
            teardownZoom = buildChart() || (() => {});
        };

        let roRaf = null;
        const scheduleRun = () => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            roRaf = requestAnimationFrame(() => {
                roRaf = null;
                run();
            });
        };

        run();
        const ro = new ResizeObserver(() => scheduleRun());
        ro.observe(el);
        return () => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            ro.disconnect();
            teardownZoom();
            el.innerHTML = "";
        };
    }, [clusteredBubbles, fillAvailableHeight]);

    if (!clusteredBubbles?.children?.length) return null;

    return (
        <Box
            ref={hostRef}
            sx={{
                width: "100%",
                ...(fillAvailableHeight
                    ? {
                          flex: "1 1 0%",
                          minHeight: 0,
                          maxHeight: "100%",
                          minWidth: 0,
                          alignSelf: "stretch",
                      }
                    : {
                          height: chartHeight,
                          minHeight: 88,
                      }),
                borderRadius: 1,
                bgcolor: "grey.100",
                overflow: "hidden",
            }}
        />
    );
}
